import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { REGISTER_COUNTRIES, type CountryOption } from '../../client/registerCountries'

type Props = {
  id: string
  name?: string
  value: string
  onChange: (name: string) => void
  error?: string
  describedBy?: string
  placeholder?: string
}

export function TradeCountrySelect({
  id,
  name = 'country',
  value,
  onChange,
  error,
  describedBy,
  placeholder = 'Search country',
}: Props) {
  const listId = useId()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [active, setActive] = useState(0)
  const rootRef = useRef<HTMLDivElement>(null)
  const selected = REGISTER_COUNTRIES.find((c) => c.name === value)

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return REGISTER_COUNTRIES
    return REGISTER_COUNTRIES.filter(
      (c) => c.name.toLowerCase().includes(q) || c.dial.replace(/\s/g, '').includes(q.replace(/\s/g, '')),
    )
  }, [query])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  function displayFor(c: CountryOption) {
    return `${c.name} (${c.dial})`
  }

  function choose(c: CountryOption | null) {
    onChange(c?.name || '')
    setQuery('')
    setOpen(false)
  }

  const inputValue = open ? query : selected ? displayFor(selected) : query

  return (
    <div className="tl-combo" ref={rootRef}>
      <input
        id={id}
        name={name}
        className="tl-input"
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={open && filtered[active] ? `${listId}-${active}` : undefined}
        aria-invalid={error ? true : undefined}
        aria-describedby={describedBy}
        autoComplete="country-name"
        placeholder={placeholder}
        value={inputValue}
        onChange={(e) => {
          setQuery(e.target.value)
          setOpen(true)
          setActive(0)
          if (!e.target.value.trim()) onChange('')
        }}
        onFocus={() => {
          setOpen(true)
          setQuery('')
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') {
            e.preventDefault()
            setOpen(true)
            setActive((i) => Math.min(i + 1, Math.max(filtered.length - 1, 0)))
          } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setActive((i) => Math.max(i - 1, 0))
          } else if (e.key === 'Enter' && open) {
            e.preventDefault()
            const hit = filtered[active]
            if (hit) choose(hit)
          } else if (e.key === 'Escape') {
            setOpen(false)
          }
        }}
      />
      {open ? (
        <ul id={listId} className="tl-combo__list" role="listbox">
          {filtered.length === 0 ? (
            <li className="tl-combo__empty">No matching country</li>
          ) : (
            filtered.slice(0, 12).map((c, i) => (
              <li key={c.name} id={`${listId}-${i}`} role="option" aria-selected={i === active}>
                <button
                  type="button"
                  className={`tl-combo__option${i === active ? ' is-active' : ''}`}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => choose(c)}
                >
                  <span>{c.name}</span>
                  <span className="tl-combo__dial">{c.dial}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
    </div>
  )
}
